import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, HelpCircle, XCircle } from 'lucide-react';
import type {
  BackgroundWorkload,
  LogHealthResponse,
  LogHealthState,
} from '@command-center/logging-sdk';
import { Badge, Card } from '@command-center/ui-system';

export interface LoggingHealthClient {
  get(): Promise<LogHealthResponse>;
}

/**
 * Operator view of whether logging is actually working.
 *
 * Logging never blocks a business operation, which means it can fail quietly.
 * This panel is the counterweight. A check that cannot be evaluated shows as
 * "unknown" rather than green — a dashboard that looks healthy because it
 * could not reach the database is worse than one that admits it does not know.
 */
export function LoggingHealthPanel({ client }: { client: LoggingHealthClient }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['logging-health'],
    queryFn: () => client.get(),
    refetchInterval: 120_000,
  });

  if (isLoading) return <Card className="h-[160px] animate-pulse" />;
  if (isError || !data) {
    return (
      <Card className="p-4 text-[13px] text-muted-foreground">
        Logging health could not be read. Treat logging status as unknown.
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {data.background && <BackgroundWorkPanel workload={data.background} />}
      <HealthChecks data={data} />
    </div>
  );
}

function HealthChecks({ data }: { data: LogHealthResponse }) {
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <StateIcon state={data.overall} />
          <h2 className="text-sm font-semibold">Logging health</h2>
          <Badge variant={badgeVariant(data.overall)}>{describe(data.overall)}</Badge>
        </div>
        <span className="text-[11px] text-muted-foreground">
          checked {new Date(data.generatedAt).toLocaleTimeString()}
        </span>
      </div>

      <ul className="mt-3 divide-y divide-border">
        {data.checks.map((check) => (
          <li key={check.id} className="flex items-start gap-2.5 py-2">
            <StateIcon state={check.state} />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium">{check.label}</div>
              <div className="text-[12px] text-muted-foreground">{check.detail}</div>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * Mirror and background workload.
 *
 * Two different sources, kept distinguishable on purpose: the queue counters
 * are live row states, the request volumes are counters the Worker incremented
 * during runs that found work. Neither is a provider billing figure and the
 * panel says so, because a number that looks authoritative and is not is worse
 * than no number.
 */
function BackgroundWorkPanel({ workload }: { workload: BackgroundWorkload }) {
  if (!workload.available) {
    return (
      <Card className="p-4 text-[13px] text-muted-foreground">
        <h2 className="text-sm font-semibold text-foreground">Mirror &amp; background work</h2>
        <p className="mt-1">
          The mirror queue could not be read. Treat pending, waiting, and blocked counts as
          unknown rather than zero.
        </p>
      </Card>
    );
  }

  const { jobs, last24h, last7d } = workload;
  const rate = workload.successRate24h;

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold">Mirror &amp; background work</h2>
          {workload.attention > 0 ? (
            <Badge variant="destructive">{workload.attention} need attention</Badge>
          ) : (
            <Badge variant="success">Nothing stuck</Badge>
          )}
        </div>
        <span className="text-[11px] text-muted-foreground">
          retry budget: {workload.maxAttempts} attempts, then blocked
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Pending" value={jobs.pending} />
        <Stat label="Waiting" value={jobs.waiting} hint="expected dependency" />
        <Stat label="Retrying" value={jobs.failed} tone={jobs.failed ? 'warn' : undefined} />
        <Stat label="Blocked" value={jobs.blocked} tone={jobs.blocked ? 'bad' : undefined} />
        <Stat label="Running" value={jobs.processing} />
        <Stat label="Synced" value={jobs.synced} />
      </div>

      {workload.oldestUnresolved && (
        <div className="mt-3 rounded-md border border-border p-2.5 text-[12px]">
          <div className="font-medium">Oldest unresolved job</div>
          <div className="mt-0.5 text-muted-foreground">
            <Link to={workload.oldestUnresolved.href} className="text-primary hover:underline">
              {workload.oldestUnresolved.subjectName}
            </Link>{' '}
            ({workload.oldestUnresolved.subjectType}) —{' '}
            {workload.oldestUnresolved.status.toLowerCase()} since{' '}
            {new Date(workload.oldestUnresolved.since).toLocaleString()} after{' '}
            {workload.oldestUnresolved.attempts} attempt(s).
            {workload.oldestUnresolved.reason ? ` ${workload.oldestUnresolved.reason}` : ''}
          </div>
        </div>
      )}

      <dl className="mt-3 space-y-1 text-[12px] text-muted-foreground">
        <Line
          label="Last 24 hours"
          value={
            `${last24h.runs} scheduler pass(es) with work · ${last24h.jobsProcessed} job(s) `
            + `processed · ${last24h.synced} synced · ${last24h.failed} failed attempt(s) · `
            + `${last24h.waiting} dependency wait(s) · ${last24h.blocked} blocked`
          }
        />
        <Line
          label="Success rate (24h)"
          value={rate === null ? 'No jobs finished in this window.' : `${Math.round(rate * 100)}%`}
        />
        <Line
          label="Request volume (24h)"
          value={
            `${last24h.airtableRequests} Airtable write(s) · ${last24h.supabaseRequests} Supabase `
            + `queue request(s) · ${last24h.activityRows} Activity row(s)`
          }
        />
        <Line
          label="Request volume (7d)"
          value={
            `${last7d.runs} pass(es) with work · ${last7d.airtableRequests} Airtable write(s) · `
            + `${last7d.supabaseRequests} Supabase queue request(s) · `
            + `${last7d.activityRows} Activity row(s)`
          }
        />
        <Line
          label="Airtable connection"
          value={
            workload.airtableConfigured
              ? 'Credentials configured.'
              : 'AIRTABLE_API_KEY or AIRTABLE_BASE_ID is missing — nothing can be mirrored.'
          }
        />
      </dl>

      <p className="mt-2 text-[11px] text-muted-foreground">
        A scheduler pass that finds no due work writes no run row and costs one short read, so
        these counts cover passes that did something. They are Worker-side counters, not
        Cloudflare, Supabase, or Airtable billing figures.
      </p>
    </Card>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint?: string;
  tone?: 'warn' | 'bad';
}) {
  const color = tone === 'bad' ? 'text-destructive' : tone === 'warn' ? 'text-warning' : '';
  return (
    <div>
      <div className={`text-lg font-semibold tabular-nums ${color}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      {hint && <div className="text-[10px] text-muted-foreground/70">{hint}</div>}
    </div>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="font-medium text-foreground">{label}:</dt>
      <dd>{value}</dd>
    </div>
  );
}

function StateIcon({ state }: { state: LogHealthState }) {
  const cls = 'mt-0.5 h-4 w-4 flex-shrink-0';
  if (state === 'ok') return <CheckCircle2 className={`${cls} text-success`} aria-label="ok" />;
  if (state === 'warn') return <AlertTriangle className={`${cls} text-warning`} aria-label="warning" />;
  if (state === 'fail') return <XCircle className={`${cls} text-destructive`} aria-label="failing" />;
  return <HelpCircle className={`${cls} text-muted-foreground`} aria-label="unknown" />;
}

function badgeVariant(state: LogHealthState): 'success' | 'warning' | 'destructive' | 'muted' {
  if (state === 'ok') return 'success';
  if (state === 'warn') return 'warning';
  if (state === 'fail') return 'destructive';
  return 'muted';
}

function describe(state: LogHealthState): string {
  if (state === 'ok') return 'Healthy';
  if (state === 'warn') return 'Needs attention';
  if (state === 'fail') return 'Failing';
  return 'Unknown';
}
